import { Button, CloseIcon, CreditCardIcon, WidgetSizeEnum } from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { areStripePaymentsEnabledForOrganization } from '../../helpers/stripe';
import { useOrganization } from '../../hooks/useOrganizations';
import { paths } from '../../navigation/paths';
import { useAllStudents } from '../../students/studentsHooks';

/**
 * Dashboard nudge for the "you have students who aren't on billing" backlog, the
 * discovery entry point for Add-to-billing. Only shows when the provider actually
 * has students AND some of them aren't on a plan yet.
 *
 * @handoff "Not on billing" is proxied by the absence of `enrollment.billingPlan`;
 * for exact accuracy, cross-check active recurring plans (invoicePlans). Dismissal
 * is session-only here, persist it per org/user and don't re-nag every login.
 */
export default function BillingNudgeCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const organization = useOrganization() as any;
  const studentsSlice = useAllStudents() as any;
  // delayed-payout-setup (PR #5419) lifts the billing gate without flipping
  // paymentsEnabled. Read it up here so the hook runs before the early return.
  const { delayedPayoutSetup } = useFlags();
  const students: any[] = studentsSlice?.list ?? [];
  const unbilledCount = students.filter((s) => !s?.enrollment?.billingPlan).length;

  // Only nudge providers who have students and some who aren't billed yet.
  if (dismissed || students.length === 0 || unbilledCount === 0) return null;

  // "Can this provider bill?" = payments verified OR the gate lifted by #5419's
  // flag. Used ONLY for this nudge's copy + routing below. Not a payout-connection
  // gate — that surface still keys off real paymentsEnabled and lives in #5419.
  const paymentsOn = areStripePaymentsEnabledForOrganization(organization);
  const canBill = paymentsOn || delayedPayoutSetup;

  return (
    <div
      className="relative flex flex-col gap-4 rounded-lg border border-blue-200 bg-blue-50 p-5 sm:flex-row sm:items-center sm:justify-between"
      data-testid="dashboard-billing-nudge"
    >
      <button
        type="button"
        aria-label={t('dashboard.billingNudge.dismissAction')}
        className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
        onClick={() => setDismissed(true)}
      >
        <CloseIcon className="size-5" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-blue-900/10">
          <CreditCardIcon className="h-5 w-5 text-blue-900" />
        </span>
        <div>
          <h3 className="text-base font-semibold text-navy-950">{t('dashboard.billingNudge.title')}</h3>
          <p className="mt-0.5 text-sm text-gray-600">
            {!canBill
              ? t('dashboard.billingNudge.paymentsDisabledDescription')
              : unbilledCount === 1
                ? t('dashboard.billingNudge.unbilledFamilyDescription')
                : t('dashboard.billingNudge.unbilledFamiliesDescription', { count: unbilledCount })}
          </p>
        </div>
      </div>

      <div className="pl-13 flex-none sm:pl-0">
        <Button
          primary
          size={WidgetSizeEnum.SMALL}
          data-testid="dashboard-billing-nudge-cta"
          onClick={() => navigate(canBill ? paths.addToBilling : paths.billingSignUp)}
        >
          {canBill ? t('dashboard.billingNudge.setupBillingAction') : t('dashboard.billingNudge.setupPaymentsAction')}
        </Button>
      </div>
    </div>
  );
}
